import { useState } from 'react';
import { Edit, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function MessagesTab({ messages, onRefresh }) {
  const [editingMessage, setEditingMessage] = useState(null);
  const [editForm, setEditForm] = useState({});

  const markRead = async (id) => {
    try {
      await api.put(`/admin/messages/${id}/read`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const handleEdit = (msg) => {
    setEditingMessage(msg.id);
    setEditForm({
      name: msg.name,
      email: msg.email,
      phone: msg.phone || '',
      message: msg.message
    });
  };

  const handleSaveEdit = async (msgId) => {
    try {
      await api.put(`/admin/messages/${msgId}`, editForm);
      toast.success('Message updated');
      setEditingMessage(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to update message');
    }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.delete(`/admin/messages/${msgId}`);
      toast.success('Message deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  return (
    <div data-testid="messages-tab">
      <h1 className="font-heading text-3xl mb-8 text-foreground">Contact Messages</h1>
      
      {messages.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No messages yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map(msg => (
            <div 
              key={msg.id} 
              className={`bg-card rounded-2xl p-6 shadow-sm border border-border ${!msg.is_read && 'border-l-4 border-l-primary'}`}
            >
              {editingMessage === msg.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editForm.phone}
                        onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Message</Label>
                    <textarea
                      className="w-full p-3 border border-border rounded-xl bg-background text-foreground"
                      rows={4}
                      value={editForm.message}
                      onChange={(e) => setEditForm(prev => ({ ...prev, message: e.target.value }))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleSaveEdit(msg.id)} className="bg-primary">
                      <Check className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button variant="outline" onClick={() => setEditingMessage(null)}>
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-foreground">{msg.name}</h3>
                      <p className="text-sm text-muted-foreground">{msg.email} {msg.phone && `• ${msg.phone}`}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-foreground mb-4">{msg.message}</p>
                  <div className="flex gap-2">
                    {!msg.is_read && (
                      <Button size="sm" variant="outline" onClick={() => markRead(msg.id)}>
                        <Check className="w-3 h-3 mr-1" />
                        Mark Read
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleEdit(msg)} data-testid={`edit-message-${msg.id}`}>
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(msg.id)} data-testid={`delete-message-${msg.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
